import React, { useState, useEffect } from "react";
import { useTheme } from "../../context/ThemeContext";
import { motion, AnimatePresence } from "framer-motion";
import HeaderSection from "../../components/HeaderSection/HeaderSection";
import BottomSection from "../../components/BottomSection/BottomSection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPercent } from "@fortawesome/free-solid-svg-icons";

const PercentageCalculator = () => {
    const [mode, setMode] = useState("percentOf");
     useTheme();
    const [input1, setInput1] = useState("");
    const [input2, setInput2] = useState("");
    const [result, setResult] = useState("Your result will appear here ✨");
    const [isCalculating, setIsCalculating] = useState(false);
    const [inputLabels, setInputLabels] = useState({
        input1: "Percentage (X)",
        input2: "Number (Y)",
    });

    // Update input labels based on selected mode
    useEffect(() => {
        switch (mode) {
            case "percentOf":
                setInputLabels({
                    input1: "Percentage (X)",
                    input2: "Number (Y)",
                });
                break;
            case "percentIs":
                setInputLabels({ input1: "Part (X)", input2: "Whole (Y)" });
                break;
            case "increase":
                setInputLabels({
                    input1: "Original Value",
                    input2: "Increased Value",
                });
                break;
            case "decrease":
                setInputLabels({
                    input1: "Original Value",
                    input2: "Decreased Value",
                });
                break;
            case "discount":
                setInputLabels({
                    input1: "Discount %",
                    input2: "Original Price",
                });
                break;
            case "tip":
                setInputLabels({
                    input1: "Bill Amount",
                    input2: "Tip Percentage",
                });
                break;
            case "profitLoss":
                setInputLabels({
                    input1: "Cost Price",
                    input2: "Selling Price",
                });
                break;
            default:
                setInputLabels({ input1: "Value 1", input2: "Value 2" });
        }
    }, [mode]);

    const calculate = () => {
        if (!input1 || !input2) {
            setResult("Please enter values in both fields");
            return;
        }

        setIsCalculating(true);

        setTimeout(() => {
            const val1 = parseFloat(input1);
            const val2 = parseFloat(input2);

            if (isNaN(val1) || isNaN(val2)) {
                setResult("Please enter valid numbers");
                setIsCalculating(false);
                return;
            }

            let res = "";
            switch (mode) {
                case "percentOf":
                    res = `${val1}% of ${val2} = ${(
                        (val1 / 100) *
                        val2
                    ).toLocaleString()}`;
                    break;
                case "percentIs":
                    res = `${val1} is ${((val1 / val2) * 100).toFixed(
                        2
                    )}% of ${val2}`;
                    break;
                case "increase":
                    res = `Increase from ${val1} to ${val2} = ${(
                        ((val2 - val1) / val1) *
                        100
                    ).toFixed(2)}%`;
                    break;
                case "decrease":
                    res = `Decrease from ${val1} to ${val2} = ${(
                        ((val1 - val2) / val1) *
                        100
                    ).toFixed(2)}%`;
                    break;
                case "discount":
                    const discountAmount = (val2 * val1) / 100;
                    res = `${val1}% off ${val2} = ${(
                        val2 - discountAmount
                    ).toLocaleString()} (You save ${discountAmount.toLocaleString()})`;
                    break;
                case "tip":
                    const tipAmount = (val1 * val2) / 100;
                    res = `${val2}% tip on ${val1} = ${tipAmount.toFixed(
                        2
                    )} (Total: ${(val1 + tipAmount).toLocaleString()})`;
                    break;
                case "profitLoss":
                    const profitLoss = (((val2 - val1) / val1) * 100).toFixed(
                        2
                    );
                    res = `${val2 >= val1 ? "Profit" : "Loss"}: ${Math.abs(
                        profitLoss
                    )}%`;
                    break;
                default:
                    res = "Please select a valid mode";
            }

            setResult(res);
            setIsCalculating(false);
        }, 600);
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            calculate();
        }
    };

    return (
        <>
            <HeaderSection toolId="percentageCalculator" />
            <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="bg-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden "
                    >
                         <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                        <h1 className="text-2xl font-bold mb-2 flex items-center">
                            <FontAwesomeIcon
                                icon={faPercent}
                                className="mr-3"
                            />
                            Percentage Calculator
                        </h1>
                        <p className="opacity-90">
                            Calculate various percentage problems with ease
                        </p>
                    </div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.4 }}
                            className="mb-6 p-6"
                        >
                            <label
                                htmlFor="mode"
                                className="block text-sm font-medium text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 mb-2"
                            >
                                Calculation Mode
                            </label>

                            <div className="relative mb-6 ">
                                <select
                                    id="mode"
                                    value={mode}
                                    onChange={(e) => setMode(e.target.value)}
                                    className="w-full p-3 rounded-lg bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 border border-gray-300 text-gray-800 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-100 dark:text-gray-100 appearance-none focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all duration-200 hover:bg-gray-100"
                                >
                                    <option value="percentOf">
                                        What is X% of Y?
                                    </option>
                                    <option value="percentIs">
                                        X is what % of Y?
                                    </option>
                                    <option value="increase">
                                        Percentage Increase
                                    </option>
                                    <option value="decrease">
                                        Percentage Decrease
                                    </option>
                                    <option value="discount">
                                        Discount Calculator
                                    </option>
                                    <option value="tip">Tip Calculator</option>
                                    <option value="profitLoss">
                                        Profit/Loss Calculator
                                    </option>
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                    <svg
                                        className="h-5 w-5 text-gray-400"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 mb-2">
                                    {inputLabels.input1}
                                </label>
                                <input
                                    type="number"
                                    value={input1}
                                    onChange={(e) => setInput1(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder="Enter value"
                                    className="w-full p-3 rounded-lg bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 border border-gray-300 text-gray-800 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-100 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all duration-200 hover:border-gray-400"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 mb-2">
                                    {inputLabels.input2}
                                </label>
                                <input
                                    type="number"
                                    value={input2}
                                    onChange={(e) => setInput2(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder="Enter value"
                                    className="w-full p-3 rounded-lg bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 border border-gray-300 text-gray-800 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-100 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all duration-200 hover:border-gray-400"
                                />
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.6 }}
                            className="mt-6"
                        >
                            <button
                                onClick={calculate}
                                disabled={isCalculating}
                                className={` md:w-[94%] w-[90%] mx-auto p-3.5 m-6 overflow-hidden rounded-lg font-bold transition-all duration-200 flex items-center justify-center
                       ${
                       isCalculating
                       ? "bg-green-700 cursor-not-allowed text-white"
                      : "bg-green-600 hover:bg-green-700  text-white shadow-md dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 hover:shadow-lg"
                       }`}
                            >
                                {isCalculating ? (
                                    <>
                                        <svg
                                            className="animate-spin h-5 w-5 text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                        </svg>
                                        Calculating...
                                    </>
                                ) : (
                                    "Calculate"
                                )}
                            </button>
                        </motion.div>

                        <AnimatePresence>
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.4 }}
                                className={`mt-6 m-6 p-4 rounded-lg border ${
                                    result.includes("Please")
                                        ? "border-red-200 bg-red-100"
                                        : "border-green-200 bg-green-100"
                                }`}
                            >
                                <h3 className="text-sm font-medium text-gray-700  mb-1">
                                    Result
                                </h3>
                                <p className="text-lg font-medium text-gray-800">
                                    {result}
                                </p>
                            </motion.div>
                        </AnimatePresence>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.8 }}
                            className="mt-6 text-center text-xs text-gray-500"
                        >
                            Press Enter to calculate
                        </motion.div>
                    </motion.div>
                </div>
            <BottomSection toolId="percentageCalculator" />
        </>
    );
};

export default PercentageCalculator;
